import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api, { brainAPI, documentLibraryAPI, emailDraftsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Mail, Send, RefreshCw, AlertCircle, Zap, ListChecks, MailCheck, Loader2,
  CalendarClock, Inbox, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  MODE_BADGES, ENGAGEMENT_STYLES, OUTGOING_TABS,
  getStatusStyle, relTime, sentAtLabel, snippetOf, initials, groupByDate, draftSortDate,
} from '../components/outbox/outboxUtils';
import { OutgoingDetail, EmptyPane } from '../components/outbox/OutgoingDetail';
import { ReplyDrawer } from '../components/outbox/ReplyDrawer';
import { EditDraftDialog } from '../components/outbox/EditDraftDialog';

export default function EmailOutbox() {
  const [allDrafts, setAllDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('outgoing'); // 'outgoing' | 'replies'
  const [activeTab, setActiveTab] = useState('draft');
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [sending, setSending] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reschedulingDraft, setReschedulingDraft] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  // Replies / Inbox (cross-lead inbound)
  const [replies, setReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [openReply, setOpenReply] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [convoLoading, setConvoLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyAiLoading, setReplyAiLoading] = useState(false);
  const [bulkScheduling, setBulkScheduling] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editAttachments, setEditAttachments] = useState([]);
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [reviewTasks, setReviewTasks] = useState([]);
  const navigate = useNavigate();

  const checkEmailConfig = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/email-config-status');
      setEmailConfig(res.data);
    } catch { /* ignore */ }
  }, []);

  const loadReviewTasks = useCallback(async () => {
    try {
      const res = await brainAPI.getPendingReviews();
      setReviewTasks(res.data.tasks || []);
    } catch { /* optional */ }
  }, []);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brainAPI.getEmailDrafts(null, null);
      setAllDrafts(res.data.drafts || []);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to load email drafts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);
  useEffect(() => { checkEmailConfig(); }, [checkEmailConfig]);
  useEffect(() => { loadReviewTasks(); }, [loadReviewTasks]);

  const handleExportSent = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brain/email-drafts/export?status=sent`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email_outreach.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sent emails exported (CSV)');
    } catch {
      toast.error('Failed to export emails');
    }
  }, []);

  const loadReplies = useCallback(async () => {
    setRepliesLoading(true);
    try {
      const res = await api.get('/api/inbox/replies?limit=100');
      setReplies(res.data?.replies || []);
      const u = await api.get('/api/inbox/unread-count');
      setUnreadReplies(u.data?.unread || 0);
    } catch (e) {
      setReplies([]);
    } finally {
      setRepliesLoading(false);
    }
  }, []);
  useEffect(() => { loadReplies(); }, [loadReplies]);
  useEffect(() => { if (mode === 'replies') loadReplies(); }, [mode, loadReplies]);

  const handleOpenReply = async (reply) => {
    setOpenReply(reply);
    setReplyText('');
    setReplyAttachments([]);
    if (!reply.is_read) {
      try { await api.post(`/api/inbox/replies/${reply.id}/read`); } catch {}
      setReplies((prev) => prev.map((r) => r.id === reply.id ? { ...r, is_read: true } : r));
      setUnreadReplies((n) => Math.max(0, n - 1));
    }
    setConversation([]);
    if (reply.thread_id) {
      setConvoLoading(true);
      try {
        const res = await api.get(`/api/inbox/threads/${reply.thread_id}`);
        setConversation(res.data?.messages || []);
      } catch { setConversation([]); }
      finally { setConvoLoading(false); }
    }
  };
  const handleSendReply = async () => {
    if (!openReply || !replyText.trim()) return;
    setReplySending(true);
    try {
      await api.post(`/api/inbox/replies/${openReply.id}/reply`, {
        body: replyText.trim(),
        attachment_doc_ids: replyAttachments.map((a) => a.id),
      });
      toast.success('Reply sent', { description: `Threaded to ${openReply.lead_name}` });
      setReplyText('');
      setReplyAttachments([]);
      if (openReply.thread_id) {
        try {
          const res = await api.get(`/api/inbox/threads/${openReply.thread_id}`);
          setConversation(res.data?.messages || []);
        } catch {}
      }
      loadReplies();
    } catch (e) {
      toast.error('Failed to send reply', { description: e?.response?.data?.detail || 'Please try again' });
    } finally {
      setReplySending(false);
    }
  };
  const handleAiDraft = async () => {
    if (!openReply) return;
    setReplyAiLoading(true);
    try {
      const res = await api.post(`/api/inbox/replies/${openReply.id}/ai-draft`, {});
      const draft = res.data?.draft || '';
      if (draft) { setReplyText(draft); toast.success('AI draft ready — review before sending'); }
      else toast.error('AI returned an empty draft');
    } catch (e) {
      toast.error('Could not draft with AI', { description: e?.response?.data?.detail || 'Please try again' });
    } finally { setReplyAiLoading(false); }
  };
  const openReplyAttachPicker = () => {
    setLoadingDocs(true);
    documentLibraryAPI.list({ per_page: 50 })
      .then((r) => setLibraryDocs(r.data?.documents || []))
      .catch(() => {}).finally(() => setLoadingDocs(false));
  };
  const toggleReplyAttachment = (doc) => {
    setReplyAttachments((prev) =>
      prev.some((a) => a.id === doc.id) ? prev.filter((a) => a.id !== doc.id) : [...prev, doc]);
  };
  const handleMarkAllRead = async () => {
    try { await api.post('/api/inbox/mark-all-read'); await loadReplies(); } catch {}
  };

  const drafts = useMemo(
    () => (activeTab === 'all' ? allDrafts : allDrafts.filter(d => d.status === activeTab)),
    [activeTab, allDrafts]
  );
  const draftGroups = useMemo(() => groupByDate(drafts, draftSortDate), [drafts]);
  const replyGroups = useMemo(() => groupByDate(replies, (r) => r.received_at), [replies]);
  const selectedDraft = useMemo(() => allDrafts.find(d => d.id === selectedDraftId) || null, [allDrafts, selectedDraftId]);

  const handleDismiss = async (id) => {
    try {
      await brainAPI.dismissEmailDraft(id);
      toast.success('Email dismissed');
      if (selectedDraftId === id) setSelectedDraftId(null);
      loadDrafts();
    } catch { toast.error('Failed to dismiss'); }
  };
  const handleSend = async (id) => {
    setSending(id);
    try {
      const res = await brainAPI.sendEmailDraft(id);
      const d = res.data;
      if (d.status === 'not_sent' || d.mode === 'needs_mailbox') {
        toast.error(d.message || 'No mailbox connected — email was not sent.');
      } else if (d.status === 'queued') {
        toast.info('Email queued — mailbox throttled, will retry shortly.');
      } else {
        const msgs = { smtp: 'Email sent via SMTP!', sendgrid: 'Email sent via SendGrid!' };
        toast.success(msgs[d.mode] || 'Email sent');
      }
      loadDrafts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setSending(null); }
  };
  const handleBulkSend = async () => {
    setBulkSending(true);
    try {
      const res = await brainAPI.bulkSendDrafts();
      const d = res.data;
      if (d.sent > 0) {
        toast.success(`${d.sent} email${d.sent !== 1 ? 's' : ''} sent (${d.primary_mode} mode)${d.failed > 0 ? `, ${d.failed} held — no mailbox` : ''}`);
      } else if (d.failed > 0) {
        toast.error(`No emails sent — ${d.failed} held because no mailbox is connected. Connect SMTP/SendGrid in Integrations.`);
      } else {
        toast.info('Nothing to send.');
      }
      loadDrafts();
    } catch { toast.error('Bulk send failed'); }
    finally { setBulkSending(false); }
  };
  const handleBulkApprove = async () => {
    try { const res = await brainAPI.bulkApproveDrafts(); toast.success(`${res.data.count} emails approved`); loadDrafts(); }
    catch { toast.error('Bulk approve failed'); }
  };
  const handleScheduleSmart = async (id) => {
    setScheduling(id);
    try {
      const res = await brainAPI.scheduleSmartSend(id);
      toast.success(`Scheduled for ${res.data.local_time} (${Math.round(res.data.confidence * 100)}% confidence)`);
      loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to schedule'); }
    finally { setScheduling(null); }
  };
  const openReschedule = (draft) => {
    const base = draft.scheduled_send_at ? new Date(draft.scheduled_send_at) : new Date(Date.now() + 3600000);
    const pad = (n) => String(n).padStart(2, '0');
    setRescheduleAt(`${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(base.getDate())}T${pad(base.getHours())}:${pad(base.getMinutes())}`);
    setReschedulingDraft(draft);
  };
  const handleRescheduleSave = async () => {
    if (!rescheduleAt || !reschedulingDraft) return;
    setRescheduleSaving(true);
    try {
      const iso = new Date(rescheduleAt).toISOString();
      await brainAPI.rescheduleEmailDraft(reschedulingDraft.id, iso);
      toast.success('Email rescheduled');
      setReschedulingDraft(null);
      loadDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule'); }
    finally { setRescheduleSaving(false); }
  };
  const handleCancelScheduled = async (id) => {
    setCancelling(id);
    try { await brainAPI.cancelScheduledEmail(id); toast.success('Scheduled send cancelled'); loadDrafts(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
    finally { setCancelling(null); }
  };
  const handleBulkScheduleSmart = async () => {
    setBulkScheduling(true);
    try { const res = await brainAPI.bulkScheduleSmart(); toast.success(`${res.data.scheduled} emails scheduled for smart send`); loadDrafts(); }
    catch { toast.error('Bulk schedule failed'); }
    finally { setBulkScheduling(false); }
  };
  const handleEditSave = async () => {
    if (!editingDraft) return;
    try {
      await brainAPI.updateEmailDraft(editingDraft.id, { subject: editSubject, body: editBody });
      if (editAttachments.length > 0 || (editingDraft.attachments || []).length > 0) {
        const docIds = editAttachments.map(a => a.id || a.doc_id).filter(Boolean);
        await emailDraftsAPI.updateAttachments(editingDraft.id, docIds);
      }
      toast.success('Draft updated');
      setEditingDraft(null);
      loadDrafts();
    } catch { toast.error('Failed to update'); }
  };
  const openEdit = (draft) => {
    setEditingDraft(draft);
    setEditSubject(draft.subject);
    setEditBody(draft.body);
    setEditAttachments(draft.attachments || []);
    setLoadingDocs(true);
    documentLibraryAPI.list({ per_page: 50 }).then(r => {
      setLibraryDocs(r.data?.documents || []);
    }).catch(() => {}).finally(() => setLoadingDocs(false));
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === drafts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(drafts.map(d => d.id)));
  };

  const counts = {
    draft: allDrafts.filter(d => d.status === 'draft').length,
    approved: allDrafts.filter(d => d.status === 'approved').length,
    scheduled: allDrafts.filter(d => d.status === 'scheduled').length,
    sent: allDrafts.filter(d => d.status === 'sent').length,
    all: allDrafts.length,
  };
  const needsMailboxCount = allDrafts.filter(d => d.status === 'needs_mailbox').length;
  const emailMode = emailConfig?.email_mode || 'demo';
  const modeBadge = MODE_BADGES[emailMode] || MODE_BADGES.demo;

  // ---------- list rows ----------
  const OutgoingRow = (draft) => {
    const ss = getStatusStyle(draft.status);
    const active = selectedDraftId === draft.id;
    return (
      <div
        key={draft.id}
        onClick={() => setSelectedDraftId(draft.id)}
        data-testid={`email-draft-${draft.id}`}
        className={`group flex items-center gap-2.5 px-3 py-2.5 border-b border-border/50 cursor-pointer transition-colors ${active ? 'bg-primary/10' : selectedIds.has(draft.id) ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
      >
        {draft.status === 'draft' && (
          <span onClick={(e) => e.stopPropagation()} className="shrink-0">
            <Checkbox checked={selectedIds.has(draft.id)} onCheckedChange={() => toggleSelect(draft.id)} data-testid={`select-draft-${draft.id}`} />
          </span>
        )}
        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${ss.dot}`} title={ss.label} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium truncate flex-1">{draft.lead?.name || 'Unknown'}</span>
            <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap" data-testid={`email-sent-time-${draft.id}`}>
              {draft.status === 'sent'
                ? (sentAtLabel(draft.sent_at || draftSortDate(draft)) || relTime(draftSortDate(draft)))
                : relTime(draftSortDate(draft))}
            </span>
          </div>
          <p className="text-[13px] truncate text-foreground/90">{draft.subject}</p>
          <p className="text-[11px] text-muted-foreground truncate">{snippetOf(draft.body)}</p>
        </div>
      </div>
    );
  };

  const ReplyRow = (r) => {
    const eng = ENGAGEMENT_STYLES[r.engagement_level];
    const EngIcon = eng?.icon;
    const active = openReply?.id === r.id;
    return (
      <div
        key={r.id}
        onClick={() => handleOpenReply(r)}
        data-testid={`reply-item-${r.id}`}
        className={`group flex items-center gap-2.5 px-3 py-2.5 border-b border-border/50 cursor-pointer transition-colors ${active ? 'bg-primary/10' : !r.is_read ? 'bg-emerald-500/[0.05] hover:bg-emerald-500/[0.08]' : 'hover:bg-muted/50'}`}
      >
        <span className={`h-2 w-2 rounded-full shrink-0 ${!r.is_read ? 'bg-emerald-500' : 'bg-transparent'}`} />
        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 text-[10px] font-semibold text-muted-foreground">
          {initials(r.lead_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-[13px] truncate flex-1 ${!r.is_read ? 'font-semibold text-foreground' : 'font-medium'}`}>{r.lead_name}</span>
            {eng && (
              <Badge variant="outline" className={`text-[9px] shrink-0 ${eng.color}`}>
                {EngIcon && <EngIcon className="h-2.5 w-2.5 mr-0.5" />}{eng.label}
              </Badge>
            )}
            <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">{relTime(r.received_at)}</span>
          </div>
          <p className={`text-[13px] truncate ${!r.is_read ? 'font-semibold text-foreground' : 'text-foreground/90'}`}>{r.subject}</p>
          <p className="text-[11px] text-muted-foreground truncate">{snippetOf(r.snippet)}</p>
        </div>
      </div>
    );
  };

  const GroupHeader = (label) => (
    <div key={`h-${label}`} className="sticky top-0 z-10 px-3 py-1 bg-muted/70 backdrop-blur-sm border-b border-border/50 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
      {label}
    </div>
  );

  return (
    <div className="h-[calc(100vh-6.5rem)] flex flex-col px-4 pt-3 pb-3" data-testid="email-outbox-page">
      {/* Compact header */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2" data-testid="outbox-title">
            <Mail className="h-5 w-5 text-primary" /> Email Workspace
          </h1>
          {/* Mode switch */}
          <div className="inline-flex items-center p-0.5 rounded-lg bg-muted border border-border" data-testid="workspace-mode-switch">
            <button onClick={() => setMode('outgoing')} data-testid="mode-outgoing"
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[13px] font-medium transition-colors ${mode === 'outgoing' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <Send className="h-3.5 w-3.5" /> Outgoing <span className="text-[11px] text-muted-foreground">{counts.all}</span>
            </button>
            <button onClick={() => setMode('replies')} data-testid="mode-replies"
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[13px] font-medium transition-colors ${mode === 'replies' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <Inbox className="h-3.5 w-3.5" /> Replies
              {unreadReplies > 0 && <Badge className="text-[10px] px-1.5 bg-emerald-500/15 text-emerald-600 border-emerald-500/20">{unreadReplies}</Badge>}
            </button>
          </div>
          <Badge variant="outline" className={`text-[10px] ${modeBadge.color}`} data-testid="email-mode-badge">
            {emailMode === 'smtp' ? <MailCheck className="h-2.5 w-2.5 mr-0.5" /> : emailMode === 'sendgrid' ? <Zap className="h-2.5 w-2.5 mr-0.5" /> : <AlertCircle className="h-2.5 w-2.5 mr-0.5" />}
            {modeBadge.label}
          </Badge>
        </div>
        <Button variant="outline" size="sm" onClick={() => { mode === 'replies' ? loadReplies() : loadDrafts(); }} data-testid="refresh-outbox-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
        {mode === 'outgoing' && (
          <Button variant="outline" size="sm" onClick={handleExportSent} data-testid="email-export-csv-btn">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        )}
      </div>

      {/* Slim alert strip (outgoing only) */}
      {mode === 'outgoing' && (needsMailboxCount > 0 || emailConfig?.email_mode === 'demo' || reviewTasks.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-3 shrink-0">
          {needsMailboxCount > 0 && (
            <button onClick={() => navigate('/integrations')} data-testid="connect-mailbox-banner"
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-orange-500/30 bg-orange-500/5 text-xs hover:bg-orange-500/10 transition-colors">
              <AlertCircle className="h-3.5 w-3.5 text-orange-500 shrink-0" />
              <span className="text-orange-600 font-medium" data-testid="connect-mailbox-title">{needsMailboxCount} email{needsMailboxCount > 1 ? 's' : ''} awaiting mailbox</span>
              <span className="text-muted-foreground">· Connect mailbox</span>
            </button>
          )}
          {emailConfig?.email_mode === 'demo' && (
            <button onClick={() => navigate('/integrations')} data-testid="smtp-setup-banner"
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 text-xs hover:bg-amber-500/10 transition-colors">
              <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="text-amber-600 font-medium">Demo mode</span>
              <span className="text-muted-foreground">· Connect SMTP/SendGrid to send real emails</span>
            </button>
          )}
          {reviewTasks.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-amber-500/25 bg-amber-500/5 text-xs" data-testid="outbox-pending-reviews-banner">
              <ListChecks className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="font-medium" data-testid="pending-reviews-count">{reviewTasks.length} Bronze {reviewTasks.length === 1 ? 'lead' : 'leads'} awaiting review</span>
              <div className="flex items-center gap-1">
                {reviewTasks.slice(0, 3).map(t => (
                  <button key={t.id} onClick={() => navigate(`/leads/${t.lead_id}`)} data-testid={`pending-review-${t.lead_id}`}
                    className="px-1.5 py-0.5 rounded bg-card border border-amber-500/20 hover:border-amber-500/40 text-[11px]">
                    {t.lead?.name || 'Lead'}
                  </button>
                ))}
                {reviewTasks.length > 3 && <span className="text-muted-foreground text-[11px]">+{reviewTasks.length - 3}</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============ MASTER-DETAIL PANES ============ */}
      <div className="flex-1 flex min-h-0 border border-border rounded-lg overflow-hidden bg-card">

        {/* LEFT: list pane */}
        <div className="w-[380px] shrink-0 border-r border-border flex flex-col min-h-0" data-testid="mail-list-pane">
          {mode === 'outgoing' ? (
            <>
              {/* folder-like tabs with counts */}
              <div className="flex items-center gap-1 px-2 py-2 border-b border-border overflow-x-auto shrink-0">
                {OUTGOING_TABS.map(t => (
                  <button key={t.key} onClick={() => { setActiveTab(t.key); }} data-testid={`tab-${t.key === 'draft' ? 'drafts' : t.key}`}
                    className={`px-2.5 py-1 rounded-md text-[12px] font-medium whitespace-nowrap transition-colors ${activeTab === t.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
                    {t.label}{counts[t.key] > 0 && <span className="ml-1 text-[10px] opacity-70">{counts[t.key]}</span>}
                  </button>
                ))}
              </div>
              {/* list toolbar */}
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border shrink-0 min-h-[40px]" data-testid="list-toolbar">
                {activeTab === 'draft' && drafts.length > 0 && (
                  <Checkbox checked={selectedIds.size === drafts.length && drafts.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                )}
                {activeTab === 'draft' && counts.draft > 0 ? (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={handleBulkApprove} data-testid="bulk-approve-btn">
                      <ListChecks className="h-3.5 w-3.5 mr-1" /> Approve all
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-purple-600 hover:bg-purple-500/10" onClick={handleBulkScheduleSmart} disabled={bulkScheduling} data-testid="bulk-schedule-btn">
                      {bulkScheduling ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5 mr-1" />} Schedule all
                    </Button>
                    <Button size="sm" className="h-7 px-2 text-[11px] bg-blue-600 hover:bg-blue-700 text-white" onClick={handleBulkSend} disabled={bulkSending} data-testid="bulk-send-btn">
                      {bulkSending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Send all ({counts.draft})
                    </Button>
                  </div>
                ) : activeTab === 'approved' && counts.approved > 0 ? (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-purple-600 hover:bg-purple-500/10" onClick={handleBulkScheduleSmart} disabled={bulkScheduling} data-testid="bulk-schedule-approved-btn">
                    {bulkScheduling ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5 mr-1" />} Smart schedule all
                  </Button>
                ) : (
                  <span className="text-[11px] text-muted-foreground">{drafts.length} message{drafts.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              {/* list */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {loading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 text-primary animate-spin mr-2" /><span className="text-sm text-muted-foreground">Loading…</span></div>
                ) : drafts.length === 0 ? (
                  <div className="py-16 px-6 text-center" data-testid="outbox-empty">
                    <Mail className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm font-medium">No emails here</p>
                    <p className="text-xs text-muted-foreground mt-1">AI drafts appear when cadences run.</p>
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/leads')} data-testid="outbox-go-pipeline-btn">Go to Pipeline</Button>
                  </div>
                ) : (
                  draftGroups.map(g => (
                    <div key={g.label}>{GroupHeader(g.label)}{g.items.map(OutgoingRow)}</div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              {/* replies toolbar */}
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border shrink-0 min-h-[40px]">
                <span className="text-[12px] font-medium flex items-center gap-1.5"><Inbox className="h-3.5 w-3.5 text-emerald-500" /> Inbound replies</span>
                {unreadReplies > 0 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                    <MailCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto min-h-0" data-testid="replies-panel">
                {repliesLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 text-emerald-500 animate-spin mr-2" /><span className="text-sm text-muted-foreground">Loading…</span></div>
                ) : replies.length === 0 ? (
                  <div className="py-16 px-6 text-center" data-testid="replies-empty">
                    <Inbox className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm font-medium">No replies yet</p>
                    <p className="text-xs text-muted-foreground mt-1">When a lead replies, it shows here and the cadence auto-pauses.</p>
                  </div>
                ) : (
                  replyGroups.map(g => (
                    <div key={g.label}>{GroupHeader(g.label)}{g.items.map(ReplyRow)}</div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* RIGHT: reading pane */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0" data-testid="mail-reading-pane">
          {mode === 'outgoing' ? (
            selectedDraft ? <OutgoingDetail
              draft={selectedDraft}
              onClose={() => setSelectedDraftId(null)}
              onEdit={openEdit}
              onSend={handleSend}
              onSmartSend={handleScheduleSmart}
              onDismiss={handleDismiss}
              onReschedule={openReschedule}
              onCancel={handleCancelScheduled}
              onViewLead={(id) => navigate(`/leads/${id}`)}
              sending={sending} scheduling={scheduling} cancelling={cancelling}
            /> : <EmptyPane icon={Send} text="Select an email to read" />
          ) : (
            openReply ? <ReplyDrawer
              reply={openReply}
              conversation={conversation}
              convoLoading={convoLoading}
              replyText={replyText}
              setReplyText={setReplyText}
              replySending={replySending}
              replyAttachments={replyAttachments}
              replyAiLoading={replyAiLoading}
              onAiDraft={handleAiDraft}
              onOpenAttachPicker={openReplyAttachPicker}
              onToggleAttachment={toggleReplyAttachment}
              loadingDocs={loadingDocs}
              libraryDocs={libraryDocs}
              onSend={handleSendReply}
              onClose={() => { setOpenReply(null); setConversation([]); }}
              onCancel={() => { setOpenReply(null); setConversation([]); setReplyText(''); setReplyAttachments([]); }}
              onViewLead={(id) => navigate(`/leads/${id}`)}
            /> : <EmptyPane icon={Inbox} text="Select a reply to read the conversation" />
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <EditDraftDialog
        draft={editingDraft}
        onClose={() => setEditingDraft(null)}
        subject={editSubject} setSubject={setEditSubject}
        body={editBody} setBody={setEditBody}
        attachments={editAttachments} setAttachments={setEditAttachments}
        libraryDocs={libraryDocs} loadingDocs={loadingDocs}
        onSave={handleEditSave}
        onGoLibrary={() => navigate('/document-library')}
      />

      {/* Reschedule */}
      <Dialog open={!!reschedulingDraft} onOpenChange={(o) => !o && setReschedulingDraft(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reschedule Email</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Pick a new send date &amp; time (your local timezone).</p>
            <Input type="datetime-local" value={rescheduleAt} onChange={(e) => setRescheduleAt(e.target.value)} data-testid="reschedule-datetime-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReschedulingDraft(null)}>Cancel</Button>
            <Button onClick={handleRescheduleSave} disabled={rescheduleSaving || !rescheduleAt} data-testid="reschedule-save-btn">
              {rescheduleSaving ? 'Saving…' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
